// ============================================
// AI 텍스트 생성 훅
// AiManager 모듈 재사용: 권한/키 로드 → 복호화 → OpenAI 직접 호출
// ============================================

import { useCallback, useEffect, useRef, useState } from 'react';
import OpenAI from 'openai';
import { v4 as uuidv4 } from 'uuid';

import FetchModule from '../Network/FetchModule';

import { decrypt, parseJsonSafely, classifyOpenAIError } from '../../../AiManager/Utils/aiUtils';
import { AI_MODELS, ERROR_CONFIG } from '../../../AiManager/Model/aiConfig';

import { buildTextPrompt } from './aiTextPrompts';

const { FAST_MODEL } = AI_MODELS;
const PERMISSION_TIMEOUT_MS = 15000;
const MAX_COMPLETION_TOKENS = 2000;

// 호출 제한 기준 — 서버 ai-log/count 요청 값 (standardTime 분 이내 limitCount 회) //
// NOTE: 임시 정책값. 서버 권위화(요청서: AI호출제한-정책-서버권위화) 후엔 서버 보유값으로 대체되어 무시됨 //
const LIMIT_COUNT = 30;
const STANDARD_TIME = 60;

// AI 로그 비차단 적재 — 200이 아니거나 네트워크 실패 시 1회 재시도 (UI 블로킹 안 함) //
async function postAiLogReliable(fetchModule, endpoint, payload) {
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      const responseData = await fetchModule.postDataVersion2(endpoint, 'POST', payload);

      if (responseData.status === 200) return;
    }
    catch (e) { /* 재시도 */ }
  }
}

// 대화 로그(conversation_log) 적재 — 성공·실패 공통, 비차단 //
function saveAiLogs({ fetchModule, sessionId, conversationId, source, requestMessage, responseMessage, elapsedMs }) {
  const conversationRequest = {
    id: conversationId,
    sessionId,
    source,
    requestMessage,
    responseMessage,
    intention: null,
    flow: 2,
    confidence: null,
    elapsedMs,
  };

  postAiLogReliable(fetchModule, 'ai-log/conversation', conversationRequest);
}

// status: 'init' | 'idle' | 'loading' | 'error' | 'permissionDenied' | 'rateLimited'
// sessionKey: 세션 구분 기준 (페이지 경로·카테고리 등). 값이 바뀌면 새 세션으로 분리 //
export default function useAiTextGenerator(sessionKey) {
  const [status, setStatus] = useState('init');
  const [error, setError] = useState(null);

  const clientRef = useRef(null);
  const mountedRef = useRef(true);
  const timedOutRef = useRef(false);
  const storeNameRef = useRef('');
  const sessionIdRef = useRef(`session_${uuidv4()}`);

  // sessionKey 변경(페이지·카테고리 전환) 시 새 세션 발급 //
  useEffect(() => {
    sessionIdRef.current = `session_${uuidv4()}`;
  }, [sessionKey]);

  // AI 권한 및 API 키 로드 → OpenAI 클라이언트 생성 //
  useEffect(() => {
    mountedRef.current = true;
    timedOutRef.current = false;

    const timeoutId = setTimeout(() => {
      timedOutRef.current = true;
      if (mountedRef.current) setStatus('permissionDenied');
    }, PERMISSION_TIMEOUT_MS);

    const fetchModule = new FetchModule();
    (
      async () => {
        try {
          const responseData = await fetchModule.getDataVersion2('notice/ai/credential', 'GET');
          if (timedOutRef.current || !mountedRef.current) return;

          if (responseData.status === 200 && responseData.data && responseData.data.apiKey) {
            const decryptedKey = (await decrypt(responseData.data.apiKey)).replace(/\s/g, '');

            if (!mountedRef.current) return;

            clientRef.current = new OpenAI({ apiKey: decryptedKey, dangerouslyAllowBrowser: true });
            storeNameRef.current = responseData.data.storeName || '';
            setStatus('idle');
          }
          else {
            setStatus('permissionDenied');
          }
        }
        catch (e) {
          if (!mountedRef.current || timedOutRef.current) return;
          setStatus('permissionDenied');
        }
        finally {
          clearTimeout(timeoutId);
        }
      }
    )();

    return () => {
      mountedRef.current = false;
      clearTimeout(timeoutId);
    };
  }, []);

  // 텍스트 생성 ({ title, content } 반환, 실패 시 null) //
  const generate = useCallback(async ({ type, userPrompt, context = {}, language }) => {
    if (!clientRef.current) {
      setStatus('permissionDenied');
      return null;
    }

    setStatus('loading');
    setError(null);

    const fetchModule = new FetchModule();
    const sessionId = sessionIdRef.current;
    const conversationId = `conv_${uuidv4()}`;

    // 호출 제한 확인 (서버 기준) — 비정상 응답·한도 초과 시 차단 //
    try {
      const responseData = await fetchModule.postDataVersion2('ai-log/count', 'POST', { limitCount: LIMIT_COUNT, standardTime: STANDARD_TIME });

      if (responseData.status === 401) {
        // 인증 만료/실패 → AI 사용 불가
        setStatus('permissionDenied');
        return null;
      }
      else if (responseData.status !== 200) {
        // 400 등 비정상 응답 → 생성 차단
        setStatus('error');
        setError({ flag: 'COUNT_ERROR', message: null });
        return null;
      }
      else if (responseData.data && responseData.data.isAvailable === false) {
        // 한도 초과 → 차단
        setStatus('rateLimited');
        setError({ flag: 'RATE_LIMIT', resetAt: responseData.data.resetAt || null });
        return null;
      }
    }
    catch (e) {
      // 제한 조회 실패(네트워크 등) → 생성 차단
      setStatus('error');
      setError({ flag: 'COUNT_ERROR', message: null });
      return null;
    }

    const prompt = buildTextPrompt({ type, userPrompt, context: { ...context, storeName: storeNameRef.current }, language });
    const startTime = Date.now();

    try {
      const response = await clientRef.current.chat.completions.create({
        model: FAST_MODEL,
        messages: [{ role: 'user', content: prompt }],
        temperature: 0.5,
        max_completion_tokens: MAX_COMPLETION_TOKENS,
      });

      const elapsedMs = Date.now() - startTime;
      const parsed = parseJsonSafely(response.choices?.[0]?.message?.content);
      const isParseFail = !parsed || typeof parsed.content !== 'string' || parsed.content.trim() === '';

      // 대화 로그 적재 (응답 성공·파싱 실패 공통) — 비차단 //
      saveAiLogs({
        fetchModule,
        sessionId,
        conversationId,
        source: type,
        requestMessage: userPrompt,
        responseMessage: isParseFail ? '' : parsed.content,
        elapsedMs,
      });

      if (isParseFail) {
        setStatus('error');
        setError({ flag: 'PARSE', message: null });

        return null;
      }

      setStatus('idle');

      return {
        title: typeof parsed.title === 'string' ? parsed.title : '',
        content: parsed.content,
      };
    }
    catch (e) {
      const flag = classifyOpenAIError(e);
      const elapsedMs = Date.now() - startTime;

      // 대화 로그 적재 (LLM 호출 실패) — 비차단 //
      saveAiLogs({
        fetchModule,
        sessionId,
        conversationId,
        source: type,
        requestMessage: userPrompt,
        responseMessage: '',
        elapsedMs,
      });

      setStatus('error');
      setError({ flag, message: ERROR_CONFIG[flag]?.message || null });

      return null;
    }
  }, []);

  return { status, error, generate };
}
